import { useQueries, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { keyBy } from "@common/helpers/collections";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import { Account, AccountRole } from "@common/types/domains/accounting/account";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { getAccounts } from "@api/accounting.api";
import { useMemoByKeys } from "./useMemoByKeys";

interface UseAccountsProps {
    entityId?: number;
    classes?: AccountClass[];
    includeDisabled?: boolean;
    excludeLinkedToFinancialAccount?: boolean;
    /**
     * @deprecated Accounts are entity-scoped; basis no longer narrows results.
     * The argument is kept for caller compatibility and is ignored.
     */
    ledgerBasis?: AccountingLedgerBasis;
    includeCurrentAccount?: boolean;
    currentAccountId?: string;
}

export function getAccountsQueryKey(entityId?: number) {
    if (entityId !== undefined) {
        return ["accounting_accounts", entityId];
    }
    return ["accounting_accounts"];
}

export function useAccounts({
    entityId,
    classes,
    includeDisabled = false,
    excludeLinkedToFinancialAccount = false,
    includeCurrentAccount = false,
    currentAccountId,
}: UseAccountsProps = {}) {
    const accountsRequest = useQuery({
        queryKey: getAccountsQueryKey(entityId),
        queryFn: () => (entityId ? getAccounts(entityId) : null),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
    });

    const accounts = useMemo(
        () =>
            (accountsRequest.data?.accounts ?? []).filter((account) => {
                if (includeCurrentAccount && account.id === currentAccountId) {
                    return true;
                }

                if (!includeDisabled && account.isDisabled) {
                    return false;
                }

                if (
                    excludeLinkedToFinancialAccount &&
                    (account.financialAccountId ||
                        account.role === AccountRole.cashOnHand)
                ) {
                    return false;
                }

                if (classes) {
                    return classes.includes(account.class);
                }

                return true;
            }),
        [
            accountsRequest.data?.accounts,
            classes,
            includeDisabled,
            excludeLinkedToFinancialAccount,
            includeCurrentAccount,
            currentAccountId,
        ],
    );

    // Keyed on `code`, never `codeText`: only `code` is immune to user edits.
    const accountsByCode = useMemo(
        () => keyBy(accounts, (account) => String(account.code)),
        [accounts],
    );

    const accountsById = useMemo(
        () => (accounts ? keyBy(accounts, (a) => a.id) : {}),
        [accounts],
    );

    return {
        isLoading: accountsRequest.isLoading,
        isFetching: accountsRequest.isFetching,
        accounts,
        accountsByCode,
        accountsById,
    };
}

interface UseAccountsByEntityResult {
    /** Accounts grouped by the entity they belong to (includes disabled). */
    accountsByEntityId: Record<number, Account[]>;
    /** Union of every entity's accounts, keyed by account id. */
    accountsById: Record<string, Account>;
    isLoading: boolean;
}

/**
 * Fetches chart-of-accounts for several entities at once (one cached query per
 * entity, reusing `useAccounts`' cache key). Used by the eliminating-entry
 * drawer, where each line's account picker is scoped to the line's chosen
 * group-member entity.
 */
export function useAccountsByEntity(
    entityIds: number[],
): UseAccountsByEntityResult {
    const results = useQueries({
        queries: entityIds.map((entityId) => ({
            queryKey: getAccountsQueryKey(entityId),
            queryFn: () => getAccounts(entityId),
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchOnMount: false,
        })),
    });

    const isLoading = results.some((result) => result.isLoading);

    // `results` is a fresh array each render; a stable key over the per-entity
    // load state lets the memo recompute only when the data actually changes.
    // The account count is folded in alongside the timestamp so data being
    // cleared (e.g. an errored query dropping `data` to undefined without
    // advancing `dataUpdatedAt`) still invalidates the memo.
    const dataStateKey = results
        .map(
            (result) =>
                `${result.dataUpdatedAt}:${result.data?.accounts.length ?? "none"}`,
        )
        .join(",");
    const accountsByEntityId = useMemoByKeys(() => {
        const map: Record<number, Account[]> = {};
        entityIds.forEach((entityId, index) => {
            map[entityId] = results[index]?.data?.accounts ?? [];
        });
        return map;
    }, [entityIds, dataStateKey]);

    const accountsById = useMemo(
        () =>
            keyBy(
                Object.values(accountsByEntityId).flat(),
                (account) => account.id,
            ),
        [accountsByEntityId],
    );

    return { accountsByEntityId, accountsById, isLoading };
}
